import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import useSuperAdminContentManagementStore from '@/stores/SuperAdminStore/SuperAdminContentManagementStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import { StandaloneLessonData } from '@/services/SuperAdminLessonService';
import {
  uploadLessonDocument,
  uploadLessonMp4,
  uploadLessonScorm,
} from '@/services/UploadService';
// Reuse the Admin edit-lesson styles (identical markup/classes).
import '../../Admin/ContentManagement/EditLessonModal.css';

interface EditLessonModalProps {
  lessonId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface LessonFormData extends Partial<StandaloneLessonData> {
  title: string;
  lessonType: 'ILTS' | 'Content-Based';
  contentType?: 'document' | 'scorm' | 'mp4' | 'url';
  lessonDuration?: string;
  description?: string;
  iltsType?: 'Online' | 'Offline';
  meetUrl?: string;
  eventVenue?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  contentUrl?: string;
}

const EditLessonModal: React.FC<EditLessonModalProps> = ({ lessonId, onClose, onSuccess }) => {
  const [lessonFormData, setLessonFormData] = useState<LessonFormData>({
    title: '',
    lessonType: 'Content-Based',
    contentType: 'document',
    lessonDuration: '',
  });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [existingFile, setExistingFile] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [durationValue, setDurationValue] = useState('');
  const [durationUnit, setDurationUnit] = useState<'mins' | 'hours' | 'secs'>('mins');

  const { fetchStandaloneLessonById, selectedLesson, updateLesson } = useSuperAdminContentManagementStore();

  // Fetch lesson data on mount
  useEffect(() => {
    const loadLesson = async () => {
      setLoading(true);
      await fetchStandaloneLessonById(lessonId);
      setLoading(false);
    };
    loadLesson();
  }, [lessonId, fetchStandaloneLessonById]);

  // Populate form when lesson data is loaded
  useEffect(() => {
    if (selectedLesson) {
      const formData: LessonFormData = {
        title: selectedLesson.title,
        lessonType: selectedLesson.lessonType,
        lessonDuration: selectedLesson.lessonDuration || '',
      };

      // Parse duration
      if (selectedLesson.lessonDuration) {
        const durationParts = selectedLesson.lessonDuration.trim().split(' ');
        if (durationParts.length === 2) {
          setDurationValue(durationParts[0]);
          setDurationUnit(durationParts[1] as 'mins' | 'hours' | 'secs');
        }
      }

      if (selectedLesson.lessonType === 'Content-Based') {
        formData.contentType = selectedLesson.contentType as any;
        formData.contentUrl = selectedLesson.lessonContentUrl || '';

        // Set existing file information
        if (selectedLesson.contentType === 'document' && selectedLesson.lessonContentDocument) {
          setExistingFile(selectedLesson.lessonContentDocument);
        } else if (selectedLesson.contentType === 'mp4' && selectedLesson.lessonContentMp4) {
          setExistingFile(selectedLesson.lessonContentMp4);
        } else if (selectedLesson.contentType === 'scorm' && selectedLesson.lessonContentScorm) {
          setExistingFile(selectedLesson.lessonContentScorm);
        }
      } else if (selectedLesson.lessonType === 'ILTS' && selectedLesson.iltsInfo) {
        formData.iltsType = selectedLesson.iltsInfo.mode;
        formData.meetUrl = selectedLesson.iltsInfo.meetUrl || '';
        formData.eventVenue = selectedLesson.iltsInfo.venue || '';
        formData.startDate = selectedLesson.iltsInfo.startDate || '';
        formData.startTime = selectedLesson.iltsInfo.startTime || '';
        formData.endDate = selectedLesson.iltsInfo.endDate || '';
        formData.endTime = selectedLesson.iltsInfo.endTime || '';
      }

      setLessonFormData(formData);
    }
  }, [selectedLesson]);

  const updateLessonFormField = (field: string, value: any) => {
    setLessonFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveLesson = async () => {
    if (!lessonFormData.title.trim()) {
      setError('Lesson title is required');
      return;
    }

    if (!lessonFormData.lessonDuration?.trim()) {
      setError('Duration is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const lessonData: StandaloneLessonData = {
        title: lessonFormData.title,
        lessonType: lessonFormData.lessonType,
        lessonDuration: lessonFormData.lessonDuration,
      };

      if (lessonFormData.lessonType === 'Content-Based') {
        lessonData.contentType = lessonFormData.contentType;
        if (lessonFormData.contentType === 'url') {
          lessonData.contentUrl = lessonFormData.contentUrl;
        }
      } else if (lessonFormData.lessonType === 'ILTS') {
        lessonData.iltsType = lessonFormData.iltsType || 'Online';
        lessonData.startDate = lessonFormData.startDate;
        lessonData.startTime = lessonFormData.startTime;
        lessonData.endDate = lessonFormData.endDate;
        lessonData.endTime = lessonFormData.endTime;

        if (lessonFormData.iltsType === 'Online') {
          lessonData.meetUrl = lessonFormData.meetUrl;
        } else {
          lessonData.eventVenue = lessonFormData.eventVenue;
        }
      }

      const success = await updateLesson(lessonId, lessonData);

      if (success) {
        // Upload new file if one was selected
        if (uploadedFile && lessonFormData.lessonType === 'Content-Based') {
          try {
            let uploadResult;

            if (lessonFormData.contentType === 'document') {
              uploadResult = await uploadLessonDocument(uploadedFile, lessonId);
            } else if (lessonFormData.contentType === 'mp4') {
              uploadResult = await uploadLessonMp4(uploadedFile, lessonId);
            } else if (lessonFormData.contentType === 'scorm') {
              uploadResult = await uploadLessonScorm(uploadedFile, lessonId);
            }

            if (uploadResult && !uploadResult.success) {
              setError('Lesson updated but file upload failed');
              setSaving(false);
              return;
            }
          } catch (uploadError: any) {
            console.error('File upload error:', uploadError);
            setError('Lesson updated but file upload failed: ' + uploadError.message);
            setSaving(false);
            return;
          }
        }

        onSuccess();
        onClose();
      } else {
        setError('Failed to update lesson');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update lesson');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-lesson-modal-overlay">
        <div className="edit-lesson-modal">
          <LoadingSpinner fullScreen size="lg" message="Loading lesson details..." />
        </div>
      </div>
    );
  }

  if (error && !selectedLesson) {
    return (
      <div className="edit-lesson-modal-overlay">
        <div className="edit-lesson-modal">
          <ErrorDisplay
            fullScreen
            title="Failed to Load Lesson"
            message={error}
            type="error"
            showRetry
            onRetry={() => fetchStandaloneLessonById(lessonId)}
          />
          <div className="edit-lesson-modal-footer">
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-lesson-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="edit-lesson-modal">
        <div className="edit-lesson-modal-header">
          <h2>Edit Lesson</h2>
          <button onClick={onClose} className="edit-lesson-close-btn">
            <X size={24} />
          </button>
        </div>

        <div className="edit-lesson-modal-content">
          {error && (
            <div className="edit-lesson-error-message">
              <p>{error}</p>
              <button onClick={() => setError(null)}>
                <X size={16} />
              </button>
            </div>
          )}

          <div className="edit-lesson-form">
            <div className="form-row">
              <div className="form-group">
                <label>Lesson Title *</label>
                <input
                  type="text"
                  value={lessonFormData.title}
                  onChange={(e) => updateLessonFormField('title', e.target.value)}
                  placeholder="Enter lesson title"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Lesson Type *</label>
                <select
                  value={lessonFormData.lessonType}
                  onChange={(e) => {
                    const newLessonType = e.target.value as 'ILTS' | 'Content-Based';
                    setLessonFormData(prev => ({
                      ...prev,
                      lessonType: newLessonType,
                      ...(newLessonType === 'ILTS' ? { iltsType: 'Online' } : { contentType: 'document' })
                    }));
                  }}
                  className="form-select"
                >
                  <option value="Content-Based">Content-Based</option>
                  <option value="ILTS">Instructor-Led Training Session (ILTS)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Duration *</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="number"
                    value={durationValue}
                    onChange={(e) => {
                      setDurationValue(e.target.value);
                      if (e.target.value) {
                        updateLessonFormField('lessonDuration', `${e.target.value} ${durationUnit}`);
                      } else {
                        updateLessonFormField('lessonDuration', '');
                      }
                    }}
                    placeholder="Enter value"
                    className="form-input"
                    style={{ flex: 2 }}
                    min="1"
                    required
                  />
                  <select
                    value={durationUnit}
                    onChange={(e) => {
                      const newUnit = e.target.value as 'mins' | 'hours' | 'secs';
                      setDurationUnit(newUnit);
                      if (durationValue) {
                        updateLessonFormField('lessonDuration', `${durationValue} ${newUnit}`);
                      }
                    }}
                    className="form-select"
                    style={{ flex: 1 }}
                  >
                    <option value="mins">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="secs">Seconds</option>
                  </select>
                </div>
              </div>
            </div>

            {lessonFormData.lessonType === 'Content-Based' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Content Type *</label>
                    <select
                      value={lessonFormData.contentType || 'document'}
                      onChange={(e) => {
                        const newContentType = e.target.value as 'document' | 'scorm' | 'mp4' | 'url';
                        setLessonFormData(prev => ({
                          ...prev,
                          contentType: newContentType,
                        }));
                        setUploadedFile(null);
                      }}
                      className="form-select"
                    >
                      <option value="document">Document (PDF/DOC)</option>
                      <option value="mp4">Video (MP4)</option>
                      <option value="scorm">SCORM Package</option>
                      <option value="url">External URL</option>
                    </select>
                  </div>
                </div>

                {lessonFormData.contentType === 'url' ? (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Content URL</label>
                      <input
                        type="url"
                        value={lessonFormData.contentUrl || ''}
                        onChange={(e) => updateLessonFormField('contentUrl', e.target.value)}
                        placeholder="https://example.com/content"
                        className="form-input"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Upload New {lessonFormData.contentType === 'mp4' ? 'Video' : lessonFormData.contentType === 'document' ? 'Document' : 'SCORM Package'} (Optional)</label>

                      {existingFile && !uploadedFile && (
                        <div className="edit-lesson-existing-file">
                          <div className="edit-lesson-existing-file-info">
                            <span className="edit-lesson-file-icon">📄</span>
                            <div className="edit-lesson-file-details">
                              <div className="edit-lesson-file-label">Current file:</div>
                              <div className="edit-lesson-file-name">{existingFile}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="edit-lesson-dropzone">
                        <input
                          type="file"
                          id="edit-content-upload"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setUploadedFile(file);
                            }
                          }}
                          accept={
                            lessonFormData.contentType === 'mp4' ? 'video/*' :
                            lessonFormData.contentType === 'document' ? '.pdf,.doc,.docx' :
                            '.zip'
                          }
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="edit-content-upload" className="edit-lesson-dropzone-label">
                          <div className="edit-lesson-dropzone-content">
                            {uploadedFile ? (
                              <div className="edit-lesson-dropzone-success">
                                ✓ New file selected: {uploadedFile.name}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setUploadedFile(null);
                                  }}
                                  className="edit-lesson-remove-file-btn"
                                >
                                  ✕ Remove
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="edit-lesson-dropzone-text">
                                  📁 Click to upload {existingFile ? 'a new' : 'a'} file
                                </div>
                                <div className="edit-lesson-dropzone-hint">
                                  {existingFile
                                    ? 'This will replace the current file'
                                    : `Upload ${lessonFormData.contentType === 'mp4' ? 'video' : lessonFormData.contentType === 'document' ? 'document' : 'SCORM package'}`
                                  }
                                </div>
                              </>
                            )}
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {lessonFormData.lessonType === 'ILTS' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Session Type *</label>
                    <select
                      value={lessonFormData.iltsType || 'Online'}
                      onChange={(e) => {
                        const newIltsType = e.target.value as 'Online' | 'Offline';
                        setLessonFormData(prev => ({
                          ...prev,
                          iltsType: newIltsType,
                          ...(newIltsType === 'Online' ? { eventVenue: '' } : { meetUrl: '' })
                        }));
                      }}
                      className="form-select"
                    >
                      <option value="Online">Online</option>
                      <option value="Offline">Offline</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date & Time</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input
                        type="date"
                        value={lessonFormData.startDate || ''}
                        onChange={(e) => updateLessonFormField('startDate', e.target.value)}
                        className="form-input"
                        style={{ flex: 1 }}
                      />
                      <input
                        type="time"
                        value={lessonFormData.startTime || ''}
                        onChange={(e) => updateLessonFormField('startTime', e.target.value)}
                        className="form-input"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>End Date & Time</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input
                        type="date"
                        value={lessonFormData.endDate || ''}
                        onChange={(e) => updateLessonFormField('endDate', e.target.value)}
                        className="form-input"
                        style={{ flex: 1 }}
                      />
                      <input
                        type="time"
                        value={lessonFormData.endTime || ''}
                        onChange={(e) => updateLessonFormField('endTime', e.target.value)}
                        className="form-input"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                </div>

                {lessonFormData.iltsType === 'Online' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Meeting URL</label>
                      <input
                        type="url"
                        value={lessonFormData.meetUrl || ''}
                        onChange={(e) => updateLessonFormField('meetUrl', e.target.value)}
                        placeholder="https://zoom.us/j/123456789"
                        className="form-input"
                      />
                    </div>
                  </div>
                )}

                {lessonFormData.iltsType === 'Offline' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Venue</label>
                      <input
                        type="text"
                        value={lessonFormData.eventVenue || ''}
                        onChange={(e) => updateLessonFormField('eventVenue', e.target.value)}
                        placeholder="Enter venue address"
                        className="form-input"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="edit-lesson-modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSaveLesson} className="btn-primary" disabled={saving}>
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLessonModal;
